import React, { useState, useEffect } from 'react';
import { Sparkles, Calendar } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_client-web-setup/artifacts/u0r810nb_6587f527-a426-4957-b4cf-1f060757bebd.jpeg";
const EVENT_DATE = new Date("2026-11-08T00:00:00+00:00");

const getTimeLeft = () => {
  const diff = EVENT_DATE - new Date();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    mins: Math.floor(diff / 60000) % 60,
    secs: Math.floor(diff / 1000) % 60
  };
};

const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!timeLeft) {
    return (
      <p className="text-2xl font-bold text-gold mt-10" data-testid="countdown-live">
        The celebration is here — see you at the table! 🎉
      </p>
    );
  }

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Minutes', value: timeLeft.mins },
    { label: 'Seconds', value: timeLeft.secs }
  ];

  return (
    <div className="mt-10" data-testid="countdown-timer">
      <p className="text-lg uppercase tracking-widest text-gold mb-4 font-semibold">Countdown to the Celebration</p>
      <div className="flex justify-center gap-3 sm:gap-6">
        {units.map((u) => (
          <div key={u.label} className="bg-magenta rounded-lg px-4 sm:px-6 py-4 min-w-[80px] sm:min-w-[100px] shadow-lg border-2 border-gold" data-testid={`countdown-${u.label.toLowerCase()}`}>
            <div className="text-3xl sm:text-5xl font-bold text-white" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
              {String(u.value).padStart(2, '0')}
            </div>
            <div className="text-xs sm:text-sm uppercase tracking-wider text-gold mt-1">{u.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const NextEvent = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [letterRef, letterVisible] = useScrollAnimation(0.15);

  return (
    <div className="min-h-screen bg-cream page-transition">
      {/* Hero Section */}
      <section ref={heroRef} className="relative py-24 px-4 bg-brown text-white text-center overflow-hidden">
        <div className={`relative z-10 max-w-4xl mx-auto ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="next-event-hero">
          <Sparkles className="w-14 h-14 mx-auto mb-4 text-gold" />
          <h1 className="text-5xl md:text-7xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
            The Pre-Christmas Edition
          </h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
          <p className="text-2xl mt-6 italic">A Curated Soulful Dining Experience</p>
          <p className="text-xl mt-4 text-gold font-semibold inline-flex items-center gap-2" data-testid="next-event-date">
            <Calendar className="w-6 h-6" />
            Sunday, 8th November 2026
          </p>
          <Countdown />
        </div>
      </section>

      {/* Announcement Letter */}
      <section ref={letterRef} className="py-20 px-4 bg-cream-dark">
        <div className={`max-w-3xl mx-auto ${letterVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="bg-white p-8 md:p-12 rounded-lg shadow-luxury text-center" data-testid="next-event-announcement">
            <img
              src={LOGO_URL}
              alt="Mama-Mam's Kitchen logo"
              className="w-32 h-32 md:w-40 md:h-40 mx-auto rounded-full shadow-lg mb-8 object-cover"
              data-testid="next-event-logo"
            />
            <div className="w-16 h-1 bg-gold mx-auto mb-8"></div>
            <h2 className="text-4xl font-bold text-brown mb-8" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
              A Message from Our Founder
            </h2>
            <div className="space-y-6 text-lg text-gray-800 leading-relaxed text-left md:text-center">
              <p>
                I&apos;m delighted to share that our next dining experience has now been confirmed for
                <strong className="text-brown"> Sunday, 8th November 2026</strong>. This Pre-Christmas
                edition is especially close to my heart — a celebration of warmth, heritage, and the
                comforting flavours that remind us of home as the festive season approaches.
              </p>
              <p>
                Mama-Mam&apos;s Kitchen has always been about more than food. It&apos;s about connection,
                storytelling, and honouring the traditions passed down through generations. This November,
                we&apos;ll gather once again around a beautifully curated table, surrounded by seasonal
                décor, nostalgic touches, and dishes inspired by the love and legacy of Mama-Mam herself.
              </p>
              <p>
                Full event details, including menu features and ticket release information, will be
                announced shortly. I&apos;m truly excited to welcome you back and to share another
                memorable evening together.
              </p>
            </div>
            <p className="text-xl font-semibold text-magenta italic mt-10" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
              With warmth,<br />
              Angella<br />
              <span className="text-base text-gray-600 not-italic font-normal">Founder, Mama-Mam&apos;s Kitchen</span>
            </p>

            <div className="mt-10 bg-magenta text-white text-center py-4 px-6 rounded-lg" data-testid="next-event-tickets-banner">
              <p className="text-xl font-bold">Tickets & Full Menu Details Announced Soon — Watch This Space!</p>
            </div>

            <div className="mt-8">
              <a
                href="/reserve"
                className="w-full inline-flex items-center justify-center gap-2 bg-brown hover:bg-magenta text-white font-semibold px-8 py-4 rounded-full text-lg transition-all shadow-lg"
                data-testid="next-event-reserve-btn"
              >
                Register Your Interest
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Message */}
      <section className="py-20 px-4 bg-white text-center">
        <div className="max-w-3xl mx-auto">
          <div className="w-16 h-1 bg-gold mx-auto mb-8"></div>
          <h2 className="text-4xl md:text-5xl font-bold text-brown mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>
            We Can&apos;t Wait to Welcome You Back
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Thank you for your patience and support. You are the heartbeat of
            Mama-Mam&apos;s Kitchen, and we cannot wait to feed you, celebrate with you, and welcome you back.
          </p>
        </div>
      </section>
    </div>
  );
};

export default NextEvent;
